import { Component, Input, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, BehaviorSubject, combineLatest } from 'rxjs';
import { filter, distinctUntilChanged, map } from 'rxjs/operators';
import { ReusableStreamProperty, shallowCompare } from '@ipreo/muni-shared';
import { BarChartConfig } from '../../../shared/models/charts/chart-config-types';
import { BarChartData } from '../../../shared/models/charts/chart-data-types';
import { GameDayViewType } from '../../../state/data/state';
import { Tranche } from '../../../gameday/data/gameDay-data.transformations';
import { GameDayRootReducerState } from '../../../state/root.state';
import { getTrancheOrdersSearchedState } from '../../../state/data/reducer';
import { TrancheSummaryData } from '../../models/tranche-summary-data';
import { TrancheSummaryParams } from '../../models/tranche-summary-params';
import { barChartDataMapper } from '../../mappers/bar-chart-data.mapper';
import {
  trancheSummaryDataMapper,
  trancheSummaryParamsMapper
} from '../../mappers/tranche-summary.mapper';

@Component({
  selector: 'muni-gameday-tranche-summary',
  templateUrl: './tranche-summary.template.html',
  styleUrls: ['./tranche-summary.styles.scss'],
  standalone: false,
})
export class TrancheSummaryComponent implements OnDestroy {
  public source: TrancheSummaryData;
  public readonly tranche$ = new BehaviorSubject<Tranche>(undefined);
  public readonly viewType$ = new BehaviorSubject<GameDayViewType>(undefined);
  public readonly config: BarChartConfig = {
    axis: {
      y: {
        formatter: d => d / 1000000,
        display: true,
        units: '$Mn',
        ticks: 5
      }
    },
    margin: {
      top: 10,
      bottom: 10,
      left: 0,
      right: -15
    },
    duration: 300,
    showLegend: false
  };

  @Input()
  public set tranche(tranche: Tranche) {
    this.tranche$.next(tranche);
  }

  @Input()
  public set viewType(viewType: GameDayViewType) {
    this.viewType$.next(viewType);
  }

  @ReusableStreamProperty
  private readonly trancheParams: Observable<
    TrancheSummaryParams
  > = combineLatest(
    this.store.pipe(select(getTrancheOrdersSearchedState)),
    this.tranche$,
    this.viewType$
  ).pipe(
    map(([trancheOrders, tranche, viewType]) =>
      trancheSummaryParamsMapper(trancheOrders, tranche, viewType)
    ),
    filter(params => !!params),
    distinctUntilChanged(shallowCompare)
  );

  // tslint:disable-next-line:member-ordering
  @ReusableStreamProperty
  public readonly details: Observable<
    TrancheSummaryData
  > = this.trancheParams.pipe(
    map(trancheSummaryDataMapper),
    filter(summaryData => !!summaryData),
    distinctUntilChanged()
  );

  // tslint:disable-next-line:member-ordering
  @ReusableStreamProperty
  public readonly chartData: Observable<BarChartData> = this.trancheParams.pipe(
    map(barChartDataMapper),
    filter(chart => !!chart),
    distinctUntilChanged()
  );

  public readonly detailsSubscription: Subscription = this.details.subscribe(
    (trancheSummary: TrancheSummaryData) => (this.source = trancheSummary)
  );

  constructor(private store: Store<GameDayRootReducerState>) {}

  public ngOnDestroy(): void {
    this.detailsSubscription.unsubscribe();
  }

  public getCommaSeparatedValues(value: string, last: boolean): string {
    return value + (last ? '' : ', ');
  }

  public subscriptionFormatter(d: number): string {
    return `${d.toFixed(1)}x`;
  }
}
